class VariantDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.drawerMenu = document.querySelector('#' + this.mainDetailsToggle.dataset.drawer);
    document.querySelector('.variant-drawer__overlay').addEventListener('click', this.onCloseButtonClick.bind(this));

    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.drawerMenu
      .querySelectorAll('.variant-drawer__close')
      .forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    this.drawerMenu
      .querySelectorAll('.variant-drawer__discard')
      .forEach((button) => button.addEventListener('click', this.onResetButtonClick.bind(this)));
    this.drawerMenu.querySelectorAll('input').forEach((radio) => {
      radio.addEventListener('click', () => this.onRadioButtonClick(radio.value));
    });

    // Listen for the React personalisation app telling us a variant was selected.
    // We update our own UI (label + radio) without emitting back, avoiding loops.
    this._onReactVariantChange = this.onReactVariantChange.bind(this);
    document.addEventListener('personalisation:variant-change', this._onReactVariantChange);
  }

  onReactVariantChange(e) {
    if (e.detail?.source !== 'react') return;

    const variantId = e.detail?.variantId;
    if (!variantId) return;

    // Each <variant-drawer> instance owns one option drawer panel.
    // Work out which option name this drawer is for by reading the panel heading.
    const optionName = this.drawerMenu.querySelector('h4')?.textContent?.trim();
    if (!optionName) return;

    // Find the variant JSON embedded by variant-radios (sibling script inside the same element)
    const variantJson = this.drawerMenu.closest('variant-radios, variant-selects')
      ?.querySelector('script[type="application/json"]')?.textContent;
    if (!variantJson) return;

    let variants;
    try { variants = JSON.parse(variantJson); } catch { return; }

    const variant = variants.find(v => v.id === variantId);
    if (!variant) return;

    // Find which slot (option1/option2/option3) corresponds to this drawer's option
    // by scanning variants for one that has each slot name matching optionName.
    // The variant JSON from Shopify doesn't include option names, so we derive the
    // slot index from the drawer's panel index in the DOM (variant-drawer-N → optionN+1).
    const drawerIndexRaw = parseInt(this.drawerMenu.id.replace('variant-drawer-', ''));
    const drawerIndex = isNaN(drawerIndexRaw) ? 0 : drawerIndexRaw;
    const optionKey = ['option1', 'option2', 'option3'][drawerIndex];
    if (!optionKey) return;

    const targetValue = variant[optionKey];
    if (!targetValue) return;

    // Update each radio in this panel: check the one matching the variant's option value.
    this.drawerMenu.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.value === targetValue && !radio.checked) {
        radio.checked = true;
        // Update the trigger <h2> label without re-emitting — avoids echo back to React.
        this.mainDetailsToggle.querySelector('h2').innerText = targetValue;
      }
    });
  }

  onSummaryClick(event) {
    const detailsElement = event.currentTarget.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(event.currentTarget.nextElementSibling, detailsElement.querySelector('button'));
      event.currentTarget.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (detailsElement.hasAttribute('open')) {
        event.preventDefault();
        this.closeMenuDrawer(event, event.currentTarget);
      } else {
        this.openMenuDrawer(event.currentTarget);
      }

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        event.currentTarget.setAttribute('aria-expanded', true);
        detailsElement.closest('.has-submenu')?.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : event.currentTarget.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    this.drawerMenu.setAttribute('open', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    this.drawerMenu.removeAttribute('open');

    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onCloseButtonClick(event) {
    this.closeMenuDrawer(event, this.mainDetailsToggle);
  }

  onResetButtonClick(event) {
    this.drawerMenu.querySelector('.variant-drawer__radio input').click();
  }

  onRadioButtonClick(val) {
    this.mainDetailsToggle.querySelector('h2').innerText = val;

    // Let the React personalisation app know the user picked a new option.
    // We emit after a short delay so variant-radios has had time to compute
    // the new full variant ID and write it to input[name="id"].
    setTimeout(() => {
      const variantInput = document.querySelector('input[name="id"].product-variant-id')
        || document.querySelector('form[action="/cart/add"] input[name="id"]');
      const variantId = variantInput ? parseInt(variantInput.value) : null;
      if (variantId) {
        document.dispatchEvent(new CustomEvent('personalisation:variant-change', {
          detail: { variantId, source: 'native' },
          bubbles: false,
        }));
      }
    }, 0);
  }

  disconnectedCallback() {
    if (this._onReactVariantChange) {
      document.removeEventListener('personalisation:variant-change', this._onReactVariantChange);
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('variant-drawer', VariantDrawer);
